import React, { useMemo, useState } from "react";
import "./carousel.css";
import Image from "next/image";
import { redirect, RedirectType } from "next/navigation";
import { routes } from "@/src/utilities";
import { useAppSelector } from "@/src/store/hooks";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import VideoHighlights from "../VideoHighlights";
import { Play } from "lucide-react";
import { heroCarouselVideos } from "@/src/utilities/constants/constants";

/**
 * @description This component is used to display a vertical carousel
 * Whitepapers are displayed in the left and right column and on click it will redirect to the whitepaper/[id] page
 * Videos are displayed in the middle column and on click it will open the video modal
 * @returns {React.FC}
 */

type Slide = { label: any; image?: any; url?: string; video_url?: string };

const HeroCarousel: React.FC = () => {
  const { whitepapers, useCases } = useAppSelector(
    (state) => state.gatedContentStateReducer
  );

  const [videoModalOpen, setVideoModalOpen] = useState(false);
  const [videoUrl, setVideoUrl] = useState("");

  const slidesLeft = useMemo(() => {
    if (!whitepapers) return [];
    const slides: Slide[] = whitepapers.slice(1, 5).map((el) => ({
      label: el?.title,
      image: el?.thumbnail?.url,
      url: `${routes.WHITEPAPERS}/${el?.documentId}`,
    }));
    return slides;
  }, [whitepapers]);

  const slidesRight = useMemo(() => {
    if (!whitepapers) return [];
    let slides: Slide[] = whitepapers?.slice(5, 10)?.map((el) => ({
      label: el?.title,
      image: el?.thumbnail?.url,
      url: `${routes.WHITEPAPERS}/${el?.documentId}`,
    }));
    slides = [...slides, ...heroCarouselVideos];
    return slides;
  }, [whitepapers]);

  return (
    <div className="w-full flex gap-2 overflow-hidden h-auto md:h-screen top-0 p-0 md:pr-4 lg:pr-44">
      <Dialog open={videoModalOpen} onOpenChange={setVideoModalOpen}>
        <DialogContent className="bg-white w-[500px]">
          <DialogHeader>
            <DialogTitle>Video</DialogTitle>
            <DialogDescription>Watch the video below</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <DialogClose>Close</DialogClose>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <VideoHighlights
        open={videoModalOpen}
        onOpenChange={setVideoModalOpen}
        video_url={videoUrl}
        image={""}
      />

      {/* Left Column */}
      <div className="md:flex hidden flex-1 relative overflow-hidden">
        <div className="flex flex-col gap-2 scroll-col">
          {[...slidesLeft, ...slidesLeft]?.map((slide, idx) => (
            <div
              key={idx}
              className="relative cursor-pointer group"
              onClick={() => {
                if (slide?.video_url) {
                  setVideoModalOpen(true);
                  setVideoUrl(slide?.video_url);
                } else {
                  redirect(slide?.url || "");
                }
              }}
            >
              <Image
                src={slide.image || null}
                alt={slide.label}
                width={800}
                height={600}
                className={`object-cover rounded-md aspect-[9/13] w-full ${
                  slide?.video_url
                    ? "brightness-75 group-hover:brightness-50 transition-all"
                    : ""
                }`}
              />
              {slide?.video_url && (
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="bg-black/60 rounded-full p-4">
                    <Play className="h-8 w-8 text-white fill-white" />
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Right Column (slightly offset) */}
      <div className="md:flex hidden flex-1 relative overflow-hidden">
        <div className="flex flex-col gap-2 scroll-col -mt-[150px]">
          {[...slidesRight, ...slidesRight]?.map((slide, idx) => (
            <div
              key={idx}
              className="relative cursor-pointer group"
              onClick={() => {
                if (slide?.video_url) {
                  setVideoModalOpen(true);
                  setVideoUrl(slide?.video_url);
                } else {
                  redirect(slide?.url || "");
                }
              }}
            >
              <Image
                src={slide.image || null}
                alt={slide.label}
                width={800}
                height={600}
                className={`object-cover rounded-md aspect-[9/13] w-full ${
                  slide?.video_url
                    ? "brightness-75 group-hover:brightness-50 transition-all"
                    : ""
                }`}
              />
              {slide?.video_url && (
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="bg-black/60 rounded-full p-4">
                    <Play className="h-8 w-8 text-white fill-white" />
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Mobile carousel */}
      <div className="flex md:hidden flex-col w-auto h-[380px] shrink-0">
        <div className="flex items-center justify-center gap-2 w-auto h-full scroll-col-horizontal">
          {[...slidesRight, ...slidesLeft, ...slidesRight, ...slidesLeft]?.map(
            (slide, idx) => (
              <div
                key={idx}
                className="relative cursor-pointer group w-auto h-full"
                onClick={() => {
                  if (slide?.video_url) {
                    setVideoModalOpen(true);
                    setVideoUrl(slide?.video_url);
                  } else {
                    redirect(slide?.url || "");
                  }
                }}
              >
                <Image
                  src={slide.image || null}
                  alt={slide.label}
                  width={200}
                  height={320}
                  className={`object-cover rounded-md aspect-[9/13] ${
                    slide?.video_url
                      ? "brightness-75 group-hover:brightness-50 transition-all"
                      : ""
                  }`}
                />
                {slide?.video_url && (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="bg-black/60 rounded-full p-4">
                      <Play className="h-8 w-8 text-white fill-white" />
                    </div>
                  </div>
                )}
              </div>
            )
          )}
        </div>
      </div>
    </div>
  );
};

export default HeroCarousel;
